import React, { useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X, AlertTriangle, Home, User, Calendar } from 'lucide-react';
import { getFunctions, httpsCallable } from 'firebase/functions';

export default function DeleteApplicationModal({ open, onClose, application, onSuccess }) {
  const [deleting, setDeleting] = useState(false);
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');

  const handleDelete = async () => {
    if (!application?.id) {
      setError('Ingen ansökan vald');
      return;
    }

    setDeleting(true);
    setError('');

    try {
      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'deleteApplicationCascade');
      await fn({ applicationId: application.id, reason });

      if (onSuccess) onSuccess();
      onClose();
    } catch (err) {
      const msg = err?.message?.includes('Endast Hyria-admin')
        ? 'Endast Hyria-admin kan radera ansökningar.'
        : 'Kunde inte ta bort ansökan. Försök igen.';
      setError(msg);
    } finally {
      setDeleting(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Ej angiven';
    return new Date(dateString).toLocaleDateString('sv-SE');
  };

  if (!application) return null;

  return (
    <Dialog open={open} onClose={onClose} className="fixed z-50 inset-0 flex items-center justify-center p-4">
      <div className="fixed inset-0 bg-black/30" aria-hidden="true" />
      <div className="relative bg-white rounded-2xl shadow-lg p-6 w-full max-w-lg mx-auto">
        <button
          className="absolute top-4 right-4 p-2 rounded-full hover:bg-zinc-100 text-zinc-500"
          onClick={onClose}
          disabled={deleting}
          aria-label="Stäng"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Header */}
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-[#222222] font-['Poppins']">
              Ta bort ansökan permanent
            </h2>
            <p className="text-sm text-[#626262] mt-1">
              Detta går inte att ångra
            </p>
          </div>
        </div>

        {/* Warning message */}
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <p className="text-sm text-red-900 font-medium mb-2">
            Följande raderas:
          </p>
          <ul className="text-sm text-red-800 space-y-1 ml-4">
            <li>• Själva ansökan</li>
            <li>• Meddelanden mellan ägare/hyresgäst/BRF</li>
            <li>• Uppladdade PDF:er (kreditrapport, kontrakt, bilagor)</li>
            <li>• Kopplade avvikelserapporter</li>
          </ul>
          <p className="text-xs text-red-700 mt-3 italic">
            Ägaren kan göra en ny ansökan för samma lägenhet efteråt.
          </p>
        </div>

        {/* Application details */}
        <div className="space-y-3 mb-6 p-4 bg-gray-50 rounded-lg">
          <div className="flex items-center gap-2 text-sm text-[#222222]">
            <Home className="w-4 h-4 text-[#626262]" />
            <span className="font-semibold">Lägenhet:</span>
            <span>{application.address || 'Ej angiven'}, Lgh {application.apartmentNumber || 'N/A'}</span>
          </div>

          {application.tenantFirstName && (
            <div className="flex items-center gap-2 text-sm text-[#222222]">
              <User className="w-4 h-4 text-[#626262]" />
              <span className="font-semibold">Hyresgäst:</span>
              <span>{application.tenantFirstName} {application.tenantLastName}</span>
            </div>
          )}

          <div className="flex items-center gap-2 text-sm text-[#222222]">
            <Calendar className="w-4 h-4 text-[#626262]" />
            <span className="font-semibold">Period:</span>
            <span>{formatDate(application.fromDate)} - {formatDate(application.toDate)}</span>
          </div>
        </div>

        {/* Optional reason */}
        <div className="mb-6">
          <label className="block text-sm font-medium text-[#222222] mb-2">
            Anledning till borttagning (valfritt)
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="T.ex. Ansökan återkallad av ägaren..."
            className="w-full bg-white border border-[#E2EAF0] rounded-lg px-3 py-2 text-[#222222] text-sm focus:outline-none focus:ring-2 focus:ring-[#62B8E9] resize-none"
            rows={3}
            disabled={deleting}
          />
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-800">{error}</p>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 border border-[#E2EAF0] text-[#626262] rounded-lg font-semibold hover:bg-[#F9FCFF] transition-colors disabled:opacity-50"
          >
            Avbryt
          </button>
          <button
            onClick={handleDelete}
            disabled={deleting}
            className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors disabled:opacity-50"
          >
            {deleting ? 'Tar bort...' : 'Ta bort permanent'}
          </button>
        </div>
      </div>
    </Dialog>
  );
}
